import { format } from 'date-fns'

import { classNames } from '../../helper'
import {
  METRICS,
  METRIC_LABELS,
  STATUS,
  asArray,
  controlOf,
  describeDecisionReason,
  formatNumber,
  formatPercent,
  isResultStale
} from '../../helper/experiments'
import { DecisionBadge, Notice } from './ExperimentBadges'
import { Explain, HELP } from './Help'

const th =
  'px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 whitespace-nowrap'
const td = 'px-3 py-2 text-sm text-gray-700 whitespace-nowrap'

const formatDateTime = (value) => {
  if (!value) return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : format(date, 'dd.MM.yyyy HH:mm')
}

const rateOf = (exposures, conversions) =>
  exposures ? conversions / exposures : null

// Kurzstand je Variante für die Liste.
export const ResultSummary = ({ result }) => {
  if (!result) {
    return <span className='text-gray-400'>Noch keine Auswertung</span>
  }
  const primary = result.primary
  return (
    <div>
      <DecisionBadge state={result.decision?.state} />
      <div className='mt-1 space-y-0.5 font-mono text-xs text-gray-600'>
        {asArray(result.variants).map((v) => (
          <div key={v.key}>
            {v.key}: {formatNumber(v.exposures)} /{' '}
            {formatNumber(v.conversions?.[primary])} ({formatPercent(v.rate)})
            {' · '}P(best) {formatPercent(v.p_best, 0)}
          </div>
        ))}
      </div>
    </div>
  )
}

const HistoryTable = ({ history, variantKeys }) => {
  const rows = [...asArray(history)].sort((a, b) =>
    (b.date || '').localeCompare(a.date || '')
  )
  const keys = [
    ...new Set([
      ...variantKeys,
      ...rows.flatMap((row) => Object.keys(row.variants || {}))
    ])
  ]

  return (
    <div className='overflow-x-auto'>
      <table className='min-w-full divide-y divide-gray-200'>
        <thead>
          <tr>
            <th className={th}>Datum</th>
            {keys.map((key) => (
              <th key={key} className={th}>
                <span className='font-mono normal-case'>{key}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className='divide-y divide-gray-100'>
          {rows.map((row) => (
            <tr key={row.date}>
              <td className={classNames(td, 'font-medium text-gray-900')}>
                {row.date}
              </td>
              {keys.map((key) => {
                const v = row.variants?.[key]
                return (
                  <td key={key} className={td}>
                    {v
                      ? `${formatNumber(v.exposures)} / ${formatNumber(v.conversions)} (${formatPercent(rateOf(v.exposures, v.conversions))})`
                      : '-'}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p className='mt-2 text-xs text-gray-500'>
        Je Auswertungstag Exposures / Conversions der primären Metrik (Rate),
        jeweils aufsummiert seit dem Start. So siehst du, ob sich der Abstand
        zwischen den Varianten stabilisiert oder noch schwankt.
      </p>
    </div>
  )
}

const ExperimentResults = ({ flag, result }) => {
  if (!result) {
    return (
      <p className='text-sm text-gray-500'>
        Noch keine Auswertung. Der Evaluator läuft täglich gegen 07:00 UTC und
        wertet nur laufende Experimente mit Unit visitor aus. Die erste Zeile
        erscheint also am Morgen nach dem Start.
      </p>
    )
  }

  const primary = result.primary || flag.decision?.primary
  const decision = result.decision || {}
  const control = controlOf(flag)
  const reason = describeDecisionReason(decision, flag)
  const variants = asArray(result.variants)
  const stale = isResultStale(flag, result)
  const winnerPending =
    !stale && decision.state === 'winner' && flag.status !== STATUS.CONCLUDED

  return (
    <div className='space-y-6'>
      <dl className='grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-3'>
        <div>
          <dt className='text-sm font-medium text-gray-500'>Entscheidung</dt>
          <dd className='mt-1'>
            <DecisionBadge state={decision.state} />
          </dd>
          <Explain className='mt-2' title='Was bedeuten die Zustände?'>
            {HELP.decisionStates}
          </Explain>
        </div>
        <div className='sm:col-span-2'>
          <dt className='text-sm font-medium text-gray-500'>Begründung</dt>
          <dd className='mt-1 text-sm text-gray-900'>{reason || '-'}</dd>
        </div>
        <div>
          <dt className='text-sm font-medium text-gray-500'>Gewinner</dt>
          <dd className='mt-1 font-mono text-sm text-gray-900'>
            {decision.winner || '-'}
          </dd>
        </div>
        <div>
          <dt className='text-sm font-medium text-gray-500'>Ausgewertet</dt>
          <dd className='mt-1 text-sm text-gray-900'>
            {formatDateTime(result.evaluated_at)}
          </dd>
        </div>
        <div>
          <dt className='text-sm font-medium text-gray-500'>Tage gelaufen</dt>
          <dd className='mt-1 text-sm text-gray-900'>
            {formatNumber(result.days_running)}
          </dd>
        </div>
      </dl>

      {stale && (
        <Notice tone='warning'>
          Dieses Ergebnis stammt aus dem Lauf vor dem letzten Start. Die neue
          Auswertung erscheint nach dem nächsten Lauf des Evaluators.
        </Notice>
      )}

      {winnerPending && (
        <Notice tone='info'>
          Der Evaluator hat einen Gewinner ermittelt, das Flag ist aber noch
          nicht beendet. Du kannst es über Beenden abschließen.
        </Notice>
      )}

      <Explain title='Wie lese ich die Tabelle?'>{HELP.results}</Explain>

      <div className='overflow-x-auto'>
        <table className='min-w-full divide-y divide-gray-200'>
          <thead>
            <tr>
              <th className={th}>Variante</th>
              <th className={th}>Exposures</th>
              {METRICS.map((metric) => (
                <th
                  key={metric}
                  className={classNames(
                    th,
                    metric === primary && 'text-indigo-700'
                  )}
                  title={metric}
                >
                  {METRIC_LABELS[metric]}
                  {metric === primary && ' (primär)'}
                </th>
              ))}
              <th className={th}>Rate primär</th>
              <th className={th}>P(best)</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-100'>
            {variants.map((v) => (
              <tr
                key={v.key}
                className={classNames(
                  decision.winner === v.key && 'bg-green-50'
                )}
              >
                <td className={classNames(td, 'font-mono text-gray-900')}>
                  {v.key}
                  {v.key === control && (
                    <span className='ml-2 font-sans text-xs text-gray-400'>
                      Control
                    </span>
                  )}
                </td>
                <td className={td}>{formatNumber(v.exposures)}</td>
                {METRICS.map((metric) => (
                  <td
                    key={metric}
                    className={classNames(
                      td,
                      metric === primary && 'font-semibold text-gray-900'
                    )}
                  >
                    {formatNumber(v.conversions?.[metric])}
                  </td>
                ))}
                <td className={classNames(td, 'font-semibold text-gray-900')}>
                  {formatPercent(v.rate)}
                </td>
                <td className={td}>{formatPercent(v.p_best)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <h4 className='text-sm font-semibold text-gray-900'>Verlauf</h4>
        {asArray(result.history).length ? (
          <div className='mt-2'>
            <HistoryTable
              history={result.history}
              variantKeys={variants.map((v) => v.key)}
            />
          </div>
        ) : (
          <p className='mt-1 text-sm text-gray-500'>Noch kein Verlauf.</p>
        )}
      </div>
    </div>
  )
}

export default ExperimentResults
